import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { ConfigStep } from '../types';
import { DEFAULT_LANG, Lang } from '../config/locale';

interface StepProgressProps {
  steps: ConfigStep[];
  currentIndex: number;
  completedCount: number;
  onStepClick?: (index: number) => void;
  lang?: Lang;
}

const StepProgress: React.FC<StepProgressProps> = ({ steps, currentIndex, completedCount, onStepClick, lang = DEFAULT_LANG }) => {
  const handleClick = (idx: number) => {
    // 완료된 단계 또는 현재 단계까지만 클릭 가능
    if (idx <= completedCount && onStepClick) {
      onStepClick(idx);
    }
  };

  return (
    <Wrapper>
      {steps.map((step, idx) => {
        const isCompleted = idx < completedCount;
        const isCurrent = idx === currentIndex;
        const isClickable = idx <= completedCount && !!onStepClick;

        return (
          <React.Fragment key={step.id}>
            {idx > 0 && (
              <Line $completed={idx <= completedCount} />
            )}
            <StepItem $clickable={isClickable} onClick={() => handleClick(idx)}>
              <Circle $completed={isCompleted} $current={isCurrent}>
                {isCompleted ? '✓' : idx + 1}
              </Circle>
              <Label $active={isCurrent || isCompleted} $current={isCurrent} $completed={isCompleted}>
                {lang === 'en' ? (step.step_name_en || step.step_name) : step.step_name}
              </Label>
            </StepItem>
          </React.Fragment>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 0;
`;

const StepItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 80px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  &:hover {
    ${({ $clickable }) => $clickable && 'opacity: 0.8;'}
  }
`;

const Circle = styled.div<{ $completed: boolean; $current: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;

  ${({ $completed, $current }) => {
    if ($completed) {
      return `
        background: ${theme.colors.success};
        color: ${theme.colors.white};
      `;
    }
    if ($current) {
      return `
        background: ${theme.colors.primary};
        color: ${theme.colors.white};
      `;
    }
    return `
      background: ${theme.colors.white};
      border: 2px solid ${theme.colors.stepperGray};
      color: ${theme.colors.stepperGray};
    `;
  }}
`;

const Line = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 3px;
  min-width: 40px;
  max-width: 80px;
  margin-top: 18px;
  border-radius: 2px;
  background: ${({ $completed }) => $completed ? theme.colors.success : theme.colors.border};
  transition: background 0.3s;
`;

const Label = styled.div<{ $active: boolean; $current: boolean; $completed: boolean }>`
  font-size: 12px;
  font-weight: ${({ $current }) => $current ? 600 : 500};
  color: ${({ $active, $current, $completed }) => {
    if ($completed) return theme.colors.success;
    if ($current) return theme.colors.primary;
    if ($active) return theme.colors.textSecondary;
    return theme.colors.textTertiary;
  }};
  text-align: center;
  white-space: nowrap;
`;

export default StepProgress;
